import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, Form } from 'react-final-form';
import Reaptcha from 'reaptcha';
import classNames from 'clsx';

import { SOURCE_ONAIR } from 'src/common/constants/sourceQueryParams';
import { REQUIRE_RECAPTCHA } from 'src/common/constants/auth/recaptcha';
import {
  RECAPTCHA_REQUIRED_ERR_MSG,
  RECAPTCHA_FAILED_ERR_MSG,
} from 'src/common/constants/auth/baseErrors';
import {
  USERNAME_EMAIL,
  REMEMBER_ME_LABEL,
  FORGOT_PASSWORD_LINK,
  SIGN_IN,
  NEXT,
  USERNAME_EMAIL_REQUIRED,
  SIGN_IN_FAILED,
} from 'src/common/constants/localizations/auth';
import {
  PASSWORD,
  PASSWORD_REQUIRED,
} from 'src/common/constants/localizations/shared';
import { selectExperiment } from 'src/common/selectors/config';
import CheckBox from '../shared/form/CheckBox';
import Input from '../shared/form/Input';
import CoralButton from '../shared/button/CoralButton';
import PillButton from '../shared/button/PillButton';
import TopLevelErrorContainer from '../shared/form/TopLevelErrorContainer';
import UnderlineLink from '../shared/link/UnderlineLink';
import ChevronRight from '../shared/svgIcons/ChevronRight';
import Checkmark from '../shared/svgIcons/Checkmark';
import partnerAuthSharedStyles, {
  inputCheckmarkSvgProps,
  inputMappedPartnerAuthStyles,
} from '../shared/styles/partnerAuthSharedStyles';

import vars from '../../vars';
import {
  fbTrack,
  fbTrackSignIn,
  fbPixelCustomEvents,
} from '../../utils/fbTrack';
import getRequiredLabel from '../../utils/form/getRequiredLabel';
import { storeFakeAuthData } from '../../actions/fakeAuth';
import { logSignInClick } from '../../actions/logging';
import checkFieldsForValidInput from '../utils/checkFieldsForValidInput';
import viewTypes from '../../constants/auth/viewTypes';
import account from '../../constants/analytics/categoryActionLabel/account';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { isRequired } from '../../utils/form/validation';
import AuthSectionDivider from './shared/AuthSectionDivider';
import getPartnerParams from './oauthProvider/getPartnerParams';
import OauthButtons from './shared/OauthButtons';
import GeneralNotices from './shared/GeneralNotices';
import SecondaryContent from './shared/SecondaryContent';

import authCss from './auth.module.scss';
import css from './sign-in-view.module.scss';

const fieldOrder = ['usernameEmail', 'password'];
const hintStyle = { fontSize: '16px' };

const svgProps = {
  ...inputCheckmarkSvgProps,
  className: authCss.checkmark,
};

function isReCaptchaError(errorResponse) {
  const faultCode = errorResponse?.response?.data?.faultCode;
  return (
    faultCode === RECAPTCHA_REQUIRED_ERR_MSG ||
    faultCode === RECAPTCHA_FAILED_ERR_MSG
  );
}

// exported for testing
export class SignInView extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      loginWithEmail: PropTypes.func.isRequired,
      loginWithOauth: PropTypes.func.isRequired,
      verifyWithEmail: PropTypes.func.isRequired,
      verifyWithOauth: PropTypes.func.isRequired,
    }),
    fakeAuthInfo: PropTypes.object.isRequired,
    logSignInClick: PropTypes.func.isRequired,
    storeFakeAuthData: PropTypes.func.isRequired,
    showReCAPTCHA: PropTypes.bool.isRequired,
    viewTypeData: PropTypes.object.isRequired,
    handleChangeToSignUp: PropTypes.func,
    failAuthNotice: PropTypes.string,
    successAuthNotice: PropTypes.string,
    handleChangeToForgotPassword: PropTypes.func,
    isDialog: PropTypes.bool,
    isPartner: PropTypes.bool,
    source: PropTypes.string,
    isPaymentFlow: PropTypes.bool,
    isVerifyFlow: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);

    this.state = {
      showReCAPTCHA: false,
      reCAPTCHAComplete: false,
    };

    this.resetRecaptcha = this.resetRecaptcha.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.handleReCaptchaResponse = this.handleReCaptchaResponse.bind(this);
    this.handleExpiredReCaptcha = this.handleExpiredReCaptcha.bind(this);
    this.handleApiError = this.handleApiError.bind(this);
    this.handleOauthSignIn = this.handleOauthSignIn.bind(this);
  }

  componentDidMount() {
    fbTrack(fbPixelCustomEvents.SignInPageVisit);
    this.setShowRecaptchaState();
  }

  setShowRecaptchaState() {
    // setting this state only when component mounts prevents rendering on server,
    // which prevents recaptcha from throwing an error when attempting to render
    // a second time on client
    this.setState({ showReCAPTCHA: this.props.showReCAPTCHA });
  }

  getSignInButton({
    pristine,
    submitting,
    submitSucceeded,
    hasValidationErrors,
  }) {
    const { isVerifyFlow, isPaymentFlow, isPartner } = this.props;
    const { getLocalizedText } = this.context;
    const SignInButton = isVerifyFlow ? PillButton : CoralButton;
    const signInButtonLabel =
      isPaymentFlow || isVerifyFlow
        ? getLocalizedText(NEXT)
        : getLocalizedText(SIGN_IN);
    const reCAPTCHAIncomplete =
      this.state.showReCAPTCHA && !this.state.reCAPTCHAComplete;
    const isSubmitDisabled =
      pristine ||
      submitting ||
      submitSucceeded ||
      hasValidationErrors ||
      reCAPTCHAIncomplete;

    const signInButtonProps = {
      id: 'signInButton',
      type: 'submit',
      label: <span>{signInButtonLabel}</span>,
      isDisabled: isSubmitDisabled,
      className: classNames(authCss.authButton, {
        [authCss.isPartnerFlow]: isPartner,
      }),
    };

    if (isVerifyFlow) {
      Object.assign(signInButtonProps, {
        id: 'verifyButton',
        labelClassName: authCss.nextButtonLabel,
        hasRightSideIcon: true,
        icon: <ChevronRight className={authCss.nextButtonIcon} />,
      });
    }

    return <SignInButton {...signInButtonProps} />;
  }

  handleReCaptchaError() {
    this.setState({ showReCAPTCHA: true });
    this.setState({ reCAPTCHAComplete: false });
    this.resetRecaptcha(); // can only submit recaptcha response for verification once
  }

  handleExpiredReCaptcha() {
    this.setState({ reCAPTCHAComplete: false });
  }

  handleReCaptchaResponse(reCAPTCHAResponse, form) {
    form.change('reCAPTCHAResponse', reCAPTCHAResponse);
    this.setState({ reCAPTCHAComplete: true });
  }

  // eslint-disable-next-line consistent-return
  async onSubmit(values) {
    const { isPartner, source, isVerifyFlow, viewTypeData } = this.props;
    const { location } = this.context;
    const {
      usernameEmail: username,
      password,
      rememberUser,
      reCAPTCHAResponse,
    } = values;

    if (!username || !password) {
      return;
    }

    const authActionParams = {
      username,
      password,
      reCAPTCHAResponse,
      rememberUser,
      source,
      params: getPartnerParams(isPartner, location),
      viewTypeData,
    };
    let shouldAttemptLogin = true;

    fbTrackSignIn();
    this.props?.logSignInClick(account.labels.email, source);

    if (isVerifyFlow) {
      shouldAttemptLogin = await this.verifyWithEmail(authActionParams);
    }

    if (!isVerifyFlow || shouldAttemptLogin) {
      return await this.loginWithEmail(authActionParams);
    }
  }

  async loginWithEmail(authActionParams) {
    const { actions, isVerifyFlow } = this.props;
    const { username, password, rememberUser } = authActionParams;

    try {
      await actions.loginWithEmail(authActionParams);
      if (!isVerifyFlow) {
        this.props.storeFakeAuthData({
          username,
          password,
          rememberUser,
          viewType: viewTypes.signIn,
        });
      }
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  async verifyWithEmail(authActionParams) {
    let isSuccessful = true;

    try {
      await this.props.actions.verifyWithEmail(authActionParams);
    } catch (error) {
      isSuccessful = false;
    }
    return isSuccessful;
  }

  async handleOauthSignIn(...args) {
    const { actions, isVerifyFlow, viewTypeData } = this.props;

    if (isVerifyFlow) {
      await actions.verifyWithOauth(...args);
    }

    actions.loginWithOauth(...args, { viewTypeData });
  }

  handleApiError(error) {
    if (isReCaptchaError(error)) {
      this.handleReCaptchaError();
    }

    return {
      base: SIGN_IN_FAILED,
    };
  }

  resetRecaptcha() {
    if (this.recaptchaInstance) {
      this.recaptchaInstance.reset();
    }
  }

  render() {
    const {
      handleChangeToSignUp,
      failAuthNotice,
      successAuthNotice,
      handleChangeToForgotPassword,
      isDialog,
      isPaymentFlow,
      isPartner,
      source,
      fakeAuthInfo,
      isVerifyFlow,
    } = this.props;

    const { username, password } = fakeAuthInfo;
    const { getLocalizedText, location } = this.context;
    const partnerParams = getPartnerParams(isPartner, location);
    const isAlexaFlow =
      partnerParams.partnerId === vars.get('ALEXA_PARTNER_ID');
    const isOnAirFlow = source === SOURCE_ONAIR;

    const additionalFieldProps = isPartner
      ? {
          ...inputMappedPartnerAuthStyles,
          floatingLabel: true,
          localizeErrorMessages: true,
          inlineErrors: true,
        }
      : {
          hintStyle,
          isBold: true,
        };

    return (
      <Form
        onSubmit={this.onSubmit}
        initialValues={{
          rememberUser: true,
        }}
      >
        {({
          errors,
          handleSubmit,
          pristine,
          submitting,
          submitSucceeded,
          submitErrors,
          hasValidationErrors,
          touched,
          form,
        }) => {
          const validFieldsMap = isPartner
            ? checkFieldsForValidInput(touched, errors)
            : null;

          return (
            <form name="signIn" onSubmit={handleSubmit}>
              <div
                data-testid="signIn"
                className={authCss.containerRestrictions}
              >
                {!isOnAirFlow && (
                  <OauthButtons
                    actionHandler={this.handleOauthSignIn}
                    loggingHandler={this.props.logSignInClick}
                    isDialog={isDialog}
                    isSignIn
                    isPartner={isPartner}
                    isPaymentFlow={isPaymentFlow}
                    source={source}
                    partnerParams={partnerParams}
                  />
                )}
                <GeneralNotices
                  key="signInGeneralNotices"
                  failAuthNotice={failAuthNotice}
                  successAuthNotice={successAuthNotice}
                />
                <TopLevelErrorContainer
                  className={authCss.largeSection}
                  errorMessages={
                    !pristine || touched ? { ...submitErrors, ...errors } : {}
                  }
                  isPartner={isPartner}
                  fieldOrder={fieldOrder}
                  touchedFields={touched}
                />
                {isPartner && <AuthSectionDivider />}
                <div className={authCss.section}>
                  <div
                    className={classNames(authCss.fieldSection, {
                      [authCss.isPartnerFlow]: isPartner,
                    })}
                  >
                    <div className={authCss.fieldSection}>
                      <div className="row">
                        <div className="col-xs-12">
                          <Field
                            component={Input}
                            className={classNames(
                              authCss.inputField,
                              'usernameField',
                              {
                                [authCss.isPartnerFlow]: isPartner,
                              },
                            )}
                            hintText={
                              !isPartner
                                ? username ||
                                  getRequiredLabel(
                                    getLocalizedText(USERNAME_EMAIL),
                                  )
                                : ''
                            }
                            autoCorrect="off"
                            autoCapitalize="none"
                            id="siUsernameEmail"
                            dataTestId="usernameEmail"
                            name="usernameEmail"
                            validate={isRequired(USERNAME_EMAIL_REQUIRED)}
                            label={
                              isPartner
                                ? username || getLocalizedText(USERNAME_EMAIL)
                                : undefined
                            }
                            isRequired
                            {...additionalFieldProps}
                          />
                          {isPartner && validFieldsMap?.usernameEmail && (
                            <Checkmark {...svgProps} />
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={classNames(authCss.fieldSection, {
                        [authCss.isPartnerFlow]: isPartner,
                      })}
                    >
                      <div className="row">
                        <div className="col-xs-12">
                          <Field
                            className={classNames(
                              authCss.inputField,
                              'passwordField',
                              {
                                [authCss.isPartnerFlow]: isPartner,
                              },
                            )}
                            hintStyle={hintStyle}
                            component={Input}
                            id="siPassword"
                            dataTestId="password"
                            type="password"
                            name="password"
                            label={
                              isPartner
                                ? password || getLocalizedText(PASSWORD)
                                : undefined
                            }
                            hintText={
                              !isPartner
                                ? password ||
                                  getRequiredLabel(getLocalizedText(PASSWORD))
                                : ''
                            }
                            validate={isRequired(PASSWORD_REQUIRED)}
                            isRequired
                            {...additionalFieldProps}
                            inputStyle={
                              isPartner
                                ? {
                                    ...inputMappedPartnerAuthStyles.inputStyle,
                                    paddingBottom: '5px',
                                  }
                                : undefined
                            }
                          />
                          {isPartner && validFieldsMap?.password && (
                            <Checkmark {...svgProps} />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-xs-12">
                        {this.state.showReCAPTCHA && (
                          <div className={css.reCaptchaContainer}>
                            <Reaptcha
                              sitekey={vars.get('RECAPTCHA_SITE_KEY')}
                              ref={(e) => {
                                this.recaptchaInstance = e;
                              }}
                              onVerify={(reCAPTCHAResponse) =>
                                this.handleReCaptchaResponse(
                                  reCAPTCHAResponse,
                                  form,
                                )
                              }
                              onExpire={this.handleExpiredReCaptcha}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {!isVerifyFlow && (
                  <div className={authCss.mediumSection}>
                    <div className={authCss.mediumSectionInner}>
                      <Field
                        component={CheckBox}
                        customSpanClass={authCss.rememberMeCheckbox}
                        customCheckmarkClass={authCss.customCheckmarkClass}
                        labelClassName={authCss.rememberMeLabel}
                        key="rememberUserCheckbox"
                        dataTestId="rememberUser"
                        id="rememberUser"
                        type="checkbox"
                        name="rememberUser"
                        label={getLocalizedText(REMEMBER_ME_LABEL)}
                        labelStyle={
                          isPartner
                            ? partnerAuthSharedStyles.labelStyle
                            : undefined
                        }
                        isBold
                      />
                      <i
                        key="siOptionsDivider"
                        className={css.authOptionsDivider}
                      />
                      <UnderlineLink
                        key="siForgotPasswordLink"
                        role="button"
                        tabIndex="0"
                        id="signInForgotPasswordQuestion"
                        onClick={handleChangeToForgotPassword}
                        underlineClassName={classNames(
                          css.underlineLink,
                          css.forgotPassword,
                          {
                            [css.partnerAuthFlow]: isPartner,
                          },
                        )}
                        className={classNames(
                          authCss.link,
                          authCss.forgotPasswordLink,
                          {
                            [authCss.isPartnerFlow]: isPartner,
                          },
                        )}
                        isDropDown
                        isNotReactRouterLink
                      >
                        {getLocalizedText(FORGOT_PASSWORD_LINK)}
                      </UnderlineLink>
                    </div>
                  </div>
                )}
                <div className={authCss.section}>
                  <div className="row">
                    <div className="col-xs-12">
                      {this.getSignInButton({
                        pristine,
                        submitting,
                        submitSucceeded,
                        hasValidationErrors,
                      })}
                      {!isVerifyFlow && !isAlexaFlow && (
                        <SecondaryContent
                          key="signInSecondaryContent"
                          isPartnerFlow={isPartner}
                          handler={handleChangeToSignUp}
                          onSignIn
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          );
        }}
      </Form>
    );
  }
}

const mapDispatchToProps = {
  storeFakeAuthData,
  logSignInClick,
};

// exported for testing
export function mapStateToProps(state) {
  return {
    fakeAuthInfo: state.fakeAuth,
    showReCAPTCHA: selectExperiment(state, REQUIRE_RECAPTCHA, false),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SignInView);
